import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AssetToolbarItem,
  DefaultColorStyle,
  DefaultToolbar,
  DrawToolbarItem,
  EraserToolbarItem,
  getDefaultColorTheme,
  HandToolbarItem,
  SelectToolbarItem,
  TextToolbarItem,
  useEditor,
  useValue,
  type TLDefaultColorStyle,
} from "tldraw";
import { tldrawIconMask } from "../tldrawIcons";

// Limited student pen palette (no black).
const PEN_COLORS: { id: TLDefaultColorStyle; label: string }[] = [
  { id: "blue", label: "Blue" },
  { id: "violet", label: "Purple" },
  { id: "red", label: "Red" },
  { id: "green", label: "Green" },
];

// Approximate rendered size of the color popover (4-swatch row + padding),
// used to keep it inside the viewport when the toolbar sits near an edge.
const POPOVER_WIDTH = 176;
const POPOVER_HEIGHT = 56;
const VIEWPORT_MARGIN = 8;

function ToolbarActionButton({
  icon,
  title,
  disabled,
  onClick,
}: {
  icon: string;
  title: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="tlui-button tlui-button__tool"
      title={title}
      disabled={disabled}
      onClick={onClick}
    >
      <div
        className="tlui-icon"
        style={{
          maskImage: tldrawIconMask(icon),
          maskSize: "100%",
          maskRepeat: "no-repeat",
          maskPosition: "center",
          backgroundColor: "currentColor",
          width: 18,
          height: 18,
        }}
      />
    </button>
  );
}

export function CanvasToolbar() {
  const editor = useEditor();
  const [colorOpen, setColorOpen] = useState(false);
  const colorBtnRef = useRef<HTMLButtonElement | null>(null);
  const [popoverPos, setPopoverPos] = useState({ left: 0, bottom: 0 });

  const canUndo = useValue("canUndo", () => editor.getCanUndo(), [editor]);
  const canRedo = useValue("canRedo", () => editor.getCanRedo(), [editor]);

  const theme = getDefaultColorTheme({
    isDarkMode: editor.user.getIsDarkMode(),
  });
  const activeColor = useValue<TLDefaultColorStyle>(
    "color",
    () => editor.getStyleForNextShape(DefaultColorStyle) ?? "blue",
    [editor],
  );
  const currentHex = (theme[activeColor] ?? theme.blue).solid;

  // Anchor the popover next to the button, preferring its right side but
  // flipping to the left and clamping so it never lands off-screen — the
  // toolbar can be repositioned by the short-screen media query in canvas.css.
  const computePopoverPos = useCallback(() => {
    const btn = colorBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
    const preferredLeft =
      r.right + 12 <= maxLeft ? r.right + 12 : r.left - 12 - POPOVER_WIDTH;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(preferredLeft, maxLeft));
    const maxBottom = window.innerHeight - POPOVER_HEIGHT - VIEWPORT_MARGIN;
    const bottom = Math.max(
      VIEWPORT_MARGIN,
      Math.min(window.innerHeight - r.bottom, maxBottom),
    );
    setPopoverPos({ left, bottom });
  }, []);

  const toggleColor = () => {
    setColorOpen((open) => {
      const next = !open;
      if (next) computePopoverPos();
      return next;
    });
  };

  // While open, keep the popover anchored on resize/orientation change and
  // let Escape dismiss it.
  useEffect(() => {
    if (!colorOpen) return;
    const onResize = () => computePopoverPos();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setColorOpen(false);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [colorOpen, computePopoverPos]);

  const selectColor = (id: TLDefaultColorStyle) => {
    editor.setStyleForNextShapes(DefaultColorStyle, id);
    if (editor.getSelectedShapeIds().length > 0) {
      // Recolor the current selection and stay on the select tool.
      editor.setStyleForSelectedShapes(DefaultColorStyle, id);
    } else {
      editor.setCurrentTool("draw");
    }
    setColorOpen(false);
  };

  return (
    <>
      <DefaultToolbar orientation="vertical">
        <ToolbarActionButton
          icon="undo"
          title="Undo"
          disabled={!canUndo}
          onClick={() => editor.undo()}
        />
        <ToolbarActionButton
          icon="redo"
          title="Redo"
          disabled={!canRedo}
          onClick={() => editor.redo()}
        />
        <SelectToolbarItem />
        <DrawToolbarItem />
        <HandToolbarItem />
        <AssetToolbarItem />
        <EraserToolbarItem />
        <TextToolbarItem />
        <button
          ref={colorBtnRef}
          type="button"
          className="tlui-button tlui-button__tool"
          aria-pressed={colorOpen}
          title="Color"
          onClick={toggleColor}
        >
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: 9999,
              backgroundColor: currentHex,
              boxShadow: "inset 0 0 0 1.5px rgba(0,0,0,0.12)",
              display: "block",
            }}
          />
        </button>
      </DefaultToolbar>

      {colorOpen &&
        createPortal(
          <>
            <div
              onClick={() => setColorOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 9998 }}
            />
            <div
              style={{
                position: "fixed",
                left: popoverPos.left,
                bottom: popoverPos.bottom,
                zIndex: 9999,
                backgroundColor: "#ffffff",
                borderRadius: 16,
                boxShadow: "rgba(0,0,0,0.16) 0px 8px 24px 0px",
                padding: 14,
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 12,
              }}
            >
              {PEN_COLORS.map((c) => {
                const selected = activeColor === c.id;
                const hex = theme[c.id].solid;
                return (
                  <button
                    key={c.id}
                    type="button"
                    title={c.label}
                    onClick={() => selectColor(c.id)}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 9999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      border: "none",
                      cursor: "pointer",
                      backgroundColor: selected
                        ? "rgba(0,0,0,0.06)"
                        : "transparent",
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 9999,
                        backgroundColor: hex,
                        boxShadow: selected
                          ? `0 0 0 2px #fff, 0 0 0 4px ${hex}`
                          : "inset 0 0 0 1px rgba(0,0,0,0.08)",
                        display: "block",
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
